import { useEffect, useMemo, useRef, useState } from 'react';
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';

interface Props {
  /** Inclusive range bounds as 'yyyy-MM-dd', or null for an open end (= all time). */
  from: string | null;
  to: string | null;
  onChange: (from: string | null, to: string | null) => void;
}

const ISO = 'yyyy-MM-dd';
const WEEK = { weekStartsOn: 0 } as const; // Sunday-first, matching the reference UI.

function parse(day: string | null): Date | null {
  if (!day) return null;
  const d = parseISO(day);
  return Number.isNaN(d.getTime()) ? null : d;
}

interface Preset {
  label: string;
  range: (today: Date) => [Date | null, Date | null];
}

const PRESETS: Preset[] = [
  { label: 'Last 7 days', range: t => [subDays(t, 6), t] },
  { label: 'Last 30 days', range: t => [subDays(t, 29), t] },
  { label: 'This week', range: t => [startOfWeek(t, WEEK), endOfWeek(t, WEEK)] },
  { label: 'Last week', range: t => { const w = subWeeks(t, 1); return [startOfWeek(w, WEEK), endOfWeek(w, WEEK)]; } },
  { label: 'This month', range: t => [startOfMonth(t), endOfMonth(t)] },
  { label: 'Last month', range: t => { const m = subMonths(t, 1); return [startOfMonth(m), endOfMonth(m)]; } },
  { label: 'All time', range: () => [null, null] },
];

function monthGrid(month: Date): Date[] {
  const start = startOfWeek(startOfMonth(month), WEEK);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

/** Label for the trigger button: a matching preset name, an explicit range, or "All time". */
function triggerLabel(from: Date | null, to: Date | null, today: Date): string {
  if (!from && !to) return 'All time';
  for (const p of PRESETS) {
    const [pf, pt] = p.range(today);
    if (pf && pt && from && to && isSameDay(pf, from) && isSameDay(pt, to)) return p.label;
  }
  if (from && to) {
    const sameYear = from.getFullYear() === to.getFullYear();
    return `${format(from, sameYear ? 'MMM d' : 'MMM d, yyyy')} – ${format(to, 'MMM d, yyyy')}`;
  }
  return from ? `From ${format(from, 'MMM d, yyyy')}` : `Until ${format(to!, 'MMM d, yyyy')}`;
}

export default function DateRangePicker({ from, to, onChange }: Props) {
  const today = useMemo(() => new Date(), []);
  const wrapRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState<Date | null>(null);
  const [draftTo, setDraftTo] = useState<Date | null>(null);
  const [viewMonth, setViewMonth] = useState<Date>(startOfMonth(today));
  // One calendar on narrow screens, two side by side otherwise.
  const [oneMonth, setOneMonth] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setOneMonth(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // Seed the draft + visible months from the committed value each time the popover opens.
  useEffect(() => {
    if (!open) return;
    const f = parse(from);
    const t = parse(to);
    setDraftFrom(f);
    setDraftTo(t);
    setViewMonth(startOfMonth(t ?? f ?? today));
  }, [open, from, to, today]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pickDay = (day: Date) => {
    // First click (or starting fresh) sets the from; the next sets the to. Clicking before
    // the current from restarts the selection from that earlier day.
    if (!draftFrom || (draftFrom && draftTo)) {
      setDraftFrom(day);
      setDraftTo(null);
    } else if (isBefore(day, draftFrom)) {
      setDraftFrom(day);
    } else {
      setDraftTo(day);
    }
  };

  const applyPreset = (p: Preset) => {
    const [pf, pt] = p.range(today);
    setDraftFrom(pf);
    setDraftTo(pt);
    if (pt ?? pf) setViewMonth(startOfMonth(pt ?? pf!));
  };

  const apply = () => {
    onChange(draftFrom ? format(draftFrom, ISO) : null, draftTo ? format(draftTo, ISO) : null);
    setOpen(false);
  };

  const inRange = (day: Date) =>
    draftFrom && draftTo && !isBefore(day, draftFrom) && !isAfter(day, draftTo);

  const label = triggerLabel(parse(from), parse(to), today);
  const months = oneMonth ? [viewMonth] : [viewMonth, addMonths(viewMonth, 1)];

  return (
    <div className="daterange" ref={wrapRef}>
      <button
        type="button"
        className="btn"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        🗓 {label}
      </button>

      {open ? (
        <div className="daterange-pop" role="dialog" aria-label="Select date range">
          <div className="daterange-cals">
            {months.map((m, idx) => (
              <div className="daterange-cal" key={idx}>
                <div className="daterange-cal-head">
                  {idx === 0 ? (
                    <button type="button" className="daterange-nav" aria-label="Previous month" onClick={() => setViewMonth(subMonths(viewMonth, 1))}>‹</button>
                  ) : <span />}
                  <strong>{format(m, 'MMMM yyyy')}</strong>
                  {idx === months.length - 1 ? (
                    <button type="button" className="daterange-nav" aria-label="Next month" onClick={() => setViewMonth(addMonths(viewMonth, 1))}>›</button>
                  ) : <span />}
                </div>
                <div className="daterange-grid">
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                    <div key={d} className="daterange-dow">{d}</div>
                  ))}
                  {monthGrid(m).map(day => {
                    const muted = !isSameMonth(day, m);
                    const isFrom = draftFrom && isSameDay(day, draftFrom);
                    const isTo = draftTo && isSameDay(day, draftTo);
                    const cls = [
                      'daterange-day',
                      muted ? 'is-muted' : '',
                      inRange(day) ? 'is-in-range' : '',
                      isFrom || isTo ? 'is-edge' : '',
                      isSameDay(day, today) ? 'is-today' : '',
                    ].filter(Boolean).join(' ');
                    return (
                      <button type="button" key={day.toISOString()} className={cls} onClick={() => pickDay(day)}>
                        {day.getDate()}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="daterange-presets">
              {PRESETS.map(p => (
                <button type="button" key={p.label} className="daterange-preset" onClick={() => applyPreset(p)}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="daterange-actions">
            <span className="muted small">
              {draftFrom ? format(draftFrom, 'MMM d, yyyy') : 'Start'} – {draftTo ? format(draftTo, 'MMM d, yyyy') : 'End'}
            </span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={apply}>Apply</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
